<template>
  <main class="grow tpl-page">
    <section class="tpl-hero">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <nav class="tpl-breadcrumb" aria-label="Breadcrumb">
          <NuxtLink :to="localePath('/templates')">Free templates</NuxtLink>
          <span aria-hidden="true">/</span>
          <span>{{ template.cardTitle }}</span>
        </nav>
        <div class="tpl-hero-grid">
          <div>
            <h1>{{ template.h1 }}</h1>
            <p class="tpl-lede">{{ template.lede }}</p>
            <FreeTemplateDownloads :files="template.files" :slug="template.slug" location="template-hero" />
            <p class="tpl-facts">Free, no email needed. {{ template.pages }} pages, in English. Updated <time :datetime="template.updatedOn">{{ useFormatDate(template.updatedOn) }}</time>.</p>
          </div>
          <figure class="tpl-preview">
            <img
              :src="template.preview.src"
              :srcset="previewSrcset"
              :sizes="previewSizes"
              :width="template.preview.width"
              :height="template.preview.height"
              :alt="template.preview.alt"
              fetchpriority="high"
              decoding="async"
            >
          </figure>
        </div>
      </div>
    </section>

    <section class="tpl-inside">
      <div class="mx-auto max-w-6xl px-4 py-12 sm:px-6 md:py-16">
        <h2>What is inside the template</h2>
        <p class="tpl-section-copy">{{ template.sections.length }} sections, {{ template.sectionsOrder || 'in the order a client reads them' }}, each with short guidance you delete before sending.</p>
        <ol class="tpl-sections">
          <li v-for="section in template.sections" :key="section.title">
            <h3>{{ section.title }}</h3>
            <p>{{ section.copy }}</p>
          </li>
        </ol>
        <div class="tpl-example">
          <h3>{{ template.example.title }}</h3>
          <p>{{ template.example.copy }}</p>
        </div>
      </div>
    </section>

    <section v-if="template.lawRules" class="tpl-laws" aria-labelledby="tpl-laws-title">
      <div class="mx-auto max-w-6xl px-4 py-12 sm:px-6 md:py-14">
        <h2 id="tpl-laws-title">{{ template.lawRules.title }}</h2>
        <p class="tpl-section-copy">{{ template.lawRules.intro }}</p>
        <div class="tpl-law-grid">
          <article v-for="rule in template.lawRules.rules" :key="rule.place" class="tpl-law-card">
            <h3>{{ rule.place }}</h3>
            <p class="tpl-law-name">{{ rule.law }}. In force: {{ rule.inForce }}.</p>
            <dl>
              <div>
                <dt>Applies to</dt>
                <dd>{{ rule.threshold }}</dd>
              </div>
              <div>
                <dt>The contract</dt>
                <dd>{{ rule.contract }}</dd>
              </div>
              <div>
                <dt>Payment due</dt>
                <dd>{{ rule.payment }}</dd>
              </div>
            </dl>
            <p class="tpl-law-sources">
              Source:
              <template v-for="(source, index) in rule.sources" :key="source.href">
                <template v-if="index > 0">; </template>
                <a :href="source.href" target="_blank" rel="noopener">{{ source.label }}</a>
              </template>
            </p>
          </article>
        </div>
        <p class="tpl-block-note">
          {{ template.lawRules.note }}
          Checked <time :datetime="template.lawRules.checkedOn">{{ useFormatDate(template.lawRules.checkedOn) }}</time>.
        </p>
      </div>
    </section>

    <section class="tpl-steps">
      <div class="mx-auto max-w-6xl px-4 py-12 sm:px-6 md:py-14">
        <h2>How to use it</h2>
        <ol class="tpl-step-list">
          <li v-for="step in template.steps" :key="step">{{ step }}</li>
        </ol>
        <!-- data-sticky-cta-hide: the phone trial bar steps aside while the page's own download buttons are on screen. -->
        <FreeTemplateDownloads class="tpl-downloads--steps" data-sticky-cta-hide :files="template.files" :slug="template.slug" location="template-steps" />
        <p class="tpl-product">
          {{ template.product.copy }}
          <NuxtLink
            :to="localePath(template.product.path)"
            data-conversion-event="template_product_click"
            :data-conversion-label="template.slug"
            data-conversion-location="template-product-line"
          >{{ template.product.linkLabel }}</NuxtLink>.
          <span class="tpl-disclosure">Our #1 pick is Workspace369.</span>
        </p>
      </div>
    </section>

    <section v-if="template.retiredResources" class="tpl-retired" aria-labelledby="tpl-retired-title">
      <div class="mx-auto max-w-6xl px-4 py-12 sm:px-6 md:py-14">
        <h2 id="tpl-retired-title">{{ template.retiredResources.title }}</h2>
        <p class="tpl-section-copy">
          {{ template.retiredResources.intro }}
          Checked <time :datetime="template.retiredResources.checkedOn">{{ useFormatDate(template.retiredResources.checkedOn) }}</time>.
        </p>
        <ul class="tpl-retired-list">
          <li v-for="item in template.retiredResources.items" :key="item.name">
            <h3>{{ item.name }}</h3>
            <p class="tpl-retired-url" translate="no">{{ item.oldUrl }}</p>
            <p>{{ item.was }}</p>
            <p><strong>Now:</strong> {{ item.now }}</p>
            <a :href="item.archiveHref" target="_blank" rel="noopener">
              Archived copy, <time :datetime="item.archivedOn">{{ useFormatDate(item.archivedOn) }}</time>
            </a>
          </li>
        </ul>
        <p class="tpl-block-note">{{ template.retiredResources.note }}</p>
      </div>
    </section>

    <ProposalVsContract v-if="template.proposalVsContract" layout="template" />

    <section class="tpl-faq">
      <div class="mx-auto max-w-6xl px-4 py-12 sm:px-6 md:py-14">
        <h2>Questions about the template</h2>
        <div class="tpl-faq-list">
          <details v-for="item in template.faq" :key="item.q" class="tpl-faq-item">
            <summary>
              <span>{{ item.q }}</span>
              <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m4.5 6 3.5 3.5L11.5 6" /></svg>
            </summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </div>
    </section>

    <section class="tpl-related">
      <div class="mx-auto max-w-6xl px-4 py-12 sm:px-6 md:py-14">
        <h2>Related guides and tools</h2>
        <nav class="tpl-related-grid" aria-label="Related Workspace369 pages">
          <NuxtLink
            v-for="link in template.relatedLinks"
            :key="link.path"
            :to="localePath(link.path)"
            class="tpl-related-card"
          >
            <span>{{ link.label }}</span>
            <span aria-hidden="true">-&gt;</span>
          </NuxtLink>
        </nav>
      </div>
    </section>

    <!-- Phones: the next trial button is the footer's, 6 to 15 screens below the hero downloads (390 px at 125% text),
         so "Start free trial" and its compact terms stay in thumb reach once the hero has scrolled away. It watches the
         whole hero, not its download row: on long titles that row starts below the first screen, and the bar would
         otherwise cover the opening before the free download is in view. -->
    <PhoneStickyCta label="Template" :location="$route.path" watch=".tpl-hero" />
  </main>
</template>

<script setup>
import { localizedUrl } from '~/data/locales';

const props = defineProps({
  template: { type: Object, required: true },
});

const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const siteUrl = 'https://workspace369.com';
const canonicalUrl = computed(() =>
  localizedUrl(siteUrl, `/templates/${props.template.slug}`, canonicalLocaleCode.value),
);

// 640w for phones, the original for wide or high-density screens (scripts/template-preview-variants.mjs).
const previewSrcset = computed(() =>
  `${props.template.preview.src.replace(/\.webp$/, '-640.webp')} 640w, ${props.template.preview.src} ${props.template.preview.width}w`,
);
const previewSizes = '(max-width: 640px) 320px, 416px';

const mimeTypes = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

useHead(() => ({
  title: props.template.metaTitle,
  meta: [
    { name: 'description', content: props.template.description },
    { name: 'twitter:title', content: props.template.metaTitle },
    { name: 'twitter:description', content: props.template.description },
    { name: 'twitter:card', content: 'summary_large_image' },
    { property: 'og:title', content: props.template.metaTitle },
    { property: 'og:description', content: props.template.description },
  ],
  link: [
    { key: 'canonical', rel: 'canonical', href: canonicalUrl.value },
    { key: 'template-preview', rel: 'preload', as: 'image', href: props.template.preview.src, imagesrcset: previewSrcset.value, imagesizes: previewSizes, fetchpriority: 'high' },
  ],
  script: [
    {
      key: 'template-document-schema',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'DigitalDocument',
        '@id': `${canonicalUrl.value}#template`,
        name: props.template.cardTitle,
        description: props.template.description,
        url: canonicalUrl.value,
        inLanguage: 'en',
        isAccessibleForFree: true,
        dateModified: props.template.updatedOn,
        image: `${siteUrl}${props.template.preview.src}`,
        publisher: { '@type': 'Organization', name: 'Workspace369', url: siteUrl },
        encoding: props.template.files.map((file) => ({
          '@type': 'MediaObject',
          contentUrl: `${siteUrl}${file.href}`,
          encodingFormat: mimeTypes[file.format],
        })),
      }),
    },
    {
      key: 'template-faq-schema',
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': `${canonicalUrl.value}#faq`,
        mainEntity: props.template.faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      }),
    },
  ],
}));
</script>

<style scoped>
.tpl-hero {
  overflow: hidden;
  background:
    radial-gradient(circle at 70% 0%, rgb(27 167 206 / 0.13), transparent 32rem),
    linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  padding: 8.5rem 0 4rem;
}

.dark .tpl-hero {
  background:
    radial-gradient(circle at 70% 10%, rgb(27 167 206 / 0.18), transparent 30rem),
    linear-gradient(180deg, #020617 0%, #0f172a 100%);
}

.tpl-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 2rem;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 700;
}

.dark .tpl-breadcrumb {
  color: #94a3b8;
}

.tpl-breadcrumb a {
  color: #0e7490;
  text-decoration: none;
}

.dark .tpl-breadcrumb a {
  color: #67e8f9;
}

.tpl-hero-grid {
  display: grid;
  gap: 2.5rem;
  align-items: center;
}

.tpl-hero h1 {
  max-width: 40rem;
  color: #020617;
  font-size: clamp(2.2rem, 5vw, 3.6rem);
  font-weight: 900;
  line-height: 1.06;
  letter-spacing: -0.01em;
  text-wrap: balance;
}

.dark .tpl-hero h1 {
  color: #ffffff;
}

.tpl-lede {
  margin-top: 1.25rem;
  max-width: 38rem;
  color: #475569;
  font-size: 1.05rem;
  line-height: 1.75;
}

.dark .tpl-lede {
  color: #cbd5e1;
}

.tpl-downloads {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 2rem;
}

:deep(.tpl-download) {
  display: inline-flex;
  min-height: 3rem;
  align-items: center;
  gap: 0.55rem;
  border-radius: 8px;
  padding: 0.75rem 1.2rem;
  font-size: 0.95rem;
  font-weight: 800;
  text-decoration: none;
  transition: background-color 150ms ease, border-color 150ms ease, transform 150ms ease;
}

:deep(.tpl-download svg) {
  width: 1.1rem;
  height: 1.1rem;
  flex-shrink: 0;
}

:deep(.tpl-download:hover) {
  transform: translateY(-1px);
}

:deep(.tpl-download:focus-visible) {
  outline: 3px solid #1ba7ce;
  outline-offset: 3px;
}

:deep(.tpl-download--primary) {
  background: #0f172a;
  color: #ffffff;
}

:deep(.tpl-download--primary:hover) {
  background: #1e293b;
}

.dark :deep(.tpl-download--primary) {
  background: #ffffff;
  color: #020617;
}

.dark :deep(.tpl-download--primary:hover) {
  background: #e2e8f0;
}

:deep(.tpl-download--secondary) {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a;
}

:deep(.tpl-download--secondary:hover) {
  border-color: #1ba7ce;
}

.dark :deep(.tpl-download--secondary) {
  border-color: #334155;
  background: #0f172a;
  color: #f1f5f9;
}

:deep(.tpl-download-size) {
  font-size: 0.78rem;
  font-weight: 650;
  opacity: 0.7;
}

.tpl-facts {
  margin-top: 1rem;
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 650;
}

.dark .tpl-facts {
  color: #94a3b8;
}

.tpl-preview {
  justify-self: center;
  width: min(100%, 26rem);
  transform: rotate(1.5deg);
}

.tpl-preview img {
  display: block;
  width: 100%;
  height: auto;
  border: 1px solid #dbe4ee;
  border-radius: 0.5rem;
  background: #ffffff;
  box-shadow: 0 30px 60px -20px rgb(15 23 42 / 0.28), 0 10px 20px -10px rgb(15 23 42 / 0.12);
}

.dark .tpl-preview img {
  border-color: #334155;
  box-shadow: 0 30px 60px -20px rgb(0 0 0 / 0.6);
}

.tpl-page h2 {
  color: #0f172a;
  font-size: clamp(1.6rem, 3vw, 2.3rem);
  font-weight: 900;
  line-height: 1.12;
}

.dark .tpl-page h2 {
  color: #f8fafc;
}

.tpl-inside {
  border-top: 1px solid #dbe4ee;
  background: #ffffff;
}

.dark .tpl-inside {
  border-color: #243247;
  background: #020617;
}

.tpl-section-copy {
  margin-top: 0.8rem;
  max-width: 42rem;
  color: #475569;
  font-size: 0.98rem;
  line-height: 1.7;
}

.dark .tpl-section-copy {
  color: #cbd5e1;
}

.tpl-sections {
  display: grid;
  gap: 1.5rem 2.5rem;
  margin-top: 2.25rem;
  counter-reset: tpl-section;
}

.tpl-sections li {
  position: relative;
  padding-left: 2.75rem;
  counter-increment: tpl-section;
}

.tpl-sections li::before {
  content: counter(tpl-section);
  position: absolute;
  top: 0;
  left: 0;
  display: grid;
  width: 1.9rem;
  height: 1.9rem;
  place-items: center;
  border-radius: 999px;
  background: #e0f2f7;
  color: #0e7490;
  font-size: 0.85rem;
  font-weight: 900;
}

.dark .tpl-sections li::before {
  background: #0d2a3a;
  color: #67e8f9;
}

.tpl-sections h3,
.tpl-example h3 {
  color: #0f172a;
  font-size: 1.02rem;
  font-weight: 850;
  line-height: 1.35;
}

.dark .tpl-sections h3,
.dark .tpl-example h3 {
  color: #f1f5f9;
}

.tpl-sections p,
.tpl-example p {
  margin-top: 0.35rem;
  color: #475569;
  font-size: 0.93rem;
  line-height: 1.65;
}

.dark .tpl-sections p,
.dark .tpl-example p {
  color: #94a3b8;
}

.tpl-example {
  margin-top: 2.5rem;
  max-width: 46rem;
  border-left: 3px solid #1ba7ce;
  border-radius: 0 10px 10px 0;
  background: #eef7fa;
  padding: 1.35rem 1.6rem;
}

.dark .tpl-example {
  background: #0d1b2d;
}

.tpl-steps {
  border-top: 1px solid #dbe4ee;
  background: #f8fafc;
}

.dark .tpl-steps {
  border-color: #243247;
  background: #0a1220;
}

.tpl-step-list {
  display: grid;
  gap: 0.8rem;
  max-width: 44rem;
  margin-top: 1.5rem;
  padding-left: 1.3rem;
  list-style: decimal;
  color: #334155;
  font-size: 0.97rem;
  line-height: 1.65;
}

.tpl-step-list li::marker {
  color: #0e7490;
  font-weight: 900;
}

.dark .tpl-step-list {
  color: #cbd5e1;
}

.dark .tpl-step-list li::marker {
  color: #67e8f9;
}

.tpl-downloads--steps {
  margin-top: 1.75rem;
}

.tpl-product {
  margin-top: 2.25rem;
  max-width: 44rem;
  color: #475569;
  font-size: 0.93rem;
  line-height: 1.7;
}

.dark .tpl-product {
  color: #94a3b8;
}

.tpl-product a {
  color: #0e7490;
  font-weight: 750;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.dark .tpl-product a {
  color: #67e8f9;
}

.tpl-disclosure {
  display: block;
  margin-top: 0.25rem;
  font-style: italic;
}

.tpl-laws,
.tpl-retired {
  border-top: 1px solid #dbe4ee;
  background: #f8fafc;
}

.tpl-retired {
  background: #ffffff;
}

.dark .tpl-laws {
  border-color: #243247;
  background: #0a1220;
}

.dark .tpl-retired {
  border-color: #243247;
  background: #020617;
}

.tpl-law-grid {
  display: grid;
  gap: 1rem;
  margin-top: 1.75rem;
}

.tpl-law-card,
.tpl-retired-list li {
  min-width: 0;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  padding: 1.25rem 1.35rem;
}

.dark .tpl-law-card,
.dark .tpl-retired-list li {
  border-color: #243247;
  background: #0f172a;
}

.tpl-law-card h3,
.tpl-retired-list h3 {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 850;
  line-height: 1.35;
}

.dark .tpl-law-card h3,
.dark .tpl-retired-list h3 {
  color: #f1f5f9;
}

.tpl-law-name {
  margin-top: 0.25rem;
  color: #0e7490;
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1.5;
}

.dark .tpl-law-name {
  color: #67e8f9;
}

.tpl-law-card dl {
  display: grid;
  gap: 0.75rem;
  margin-top: 1rem;
}

.tpl-law-card dt {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.tpl-law-card dd,
.tpl-retired-list p {
  margin-top: 0.2rem;
  color: #475569;
  font-size: 0.9rem;
  line-height: 1.6;
}

.dark .tpl-law-card dt {
  color: #94a3b8;
}

.dark .tpl-law-card dd,
.dark .tpl-retired-list p {
  color: #cbd5e1;
}

.tpl-retired-list strong {
  color: #0f172a;
  font-weight: 750;
}

.dark .tpl-retired-list strong {
  color: #f1f5f9;
}

.tpl-law-sources {
  margin-top: 1rem;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.6;
}

.dark .tpl-law-sources {
  color: #94a3b8;
}

.tpl-law-sources a,
.tpl-retired-list a {
  color: #0e7490;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
  overflow-wrap: anywhere;
}

.dark .tpl-law-sources a,
.dark .tpl-retired-list a {
  color: #67e8f9;
}

.tpl-retired-list {
  display: grid;
  gap: 1rem;
  margin-top: 1.75rem;
}

.tpl-retired-list a {
  display: inline-block;
  margin-top: 0.6rem;
  font-size: 0.88rem;
}

.tpl-retired-url {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8rem !important;
  overflow-wrap: anywhere;
}

.tpl-block-note {
  margin-top: 1.5rem;
  max-width: 46rem;
  color: #64748b;
  font-size: 0.88rem;
  line-height: 1.65;
}

.dark .tpl-block-note {
  color: #94a3b8;
}

.tpl-faq {
  border-top: 1px solid #dbe4ee;
  background: #ffffff;
}

.dark .tpl-faq {
  border-color: #243247;
  background: #020617;
}

.tpl-faq-list {
  display: grid;
  gap: 0.75rem;
  max-width: 52rem;
  margin-top: 1.75rem;
}

.tpl-faq-item {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
}

.dark .tpl-faq-item {
  border-color: #243247;
  background: #0f172a;
}

.tpl-faq-item[open] {
  border-color: rgb(27 167 206 / 0.45);
}

.tpl-faq-item summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.2rem;
  cursor: pointer;
  list-style: none;
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 700;
}

.dark .tpl-faq-item summary {
  color: #f1f5f9;
}

.tpl-faq-item summary::-webkit-details-marker {
  display: none;
}

.tpl-faq-item summary svg {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: #1ba7ce;
  transition: transform 160ms ease;
}

.tpl-faq-item[open] summary svg {
  transform: rotate(180deg);
}

.tpl-faq-item p {
  padding: 0 1.2rem 1.1rem;
  color: #475569;
  font-size: 0.9rem;
  line-height: 1.7;
}

.dark .tpl-faq-item p {
  color: #94a3b8;
}

.tpl-related {
  border-top: 1px solid #dbe4ee;
  background: #f8fafc;
}

.dark .tpl-related {
  border-color: #243247;
  background: #0a1220;
}

.tpl-related-grid {
  display: grid;
  gap: 0.9rem;
  margin-top: 1.75rem;
}

.tpl-related-card {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  padding: 0.95rem 1.2rem;
  color: #0f172a;
  font-size: 0.92rem;
  font-weight: 750;
  text-decoration: none;
  transition: border-color 150ms ease;
}

.dark .tpl-related-card {
  border-color: #243247;
  background: #07101f;
  color: #e2e8f0;
}

.tpl-related-card:hover {
  border-color: rgb(27 167 206 / 0.5);
}

.tpl-related-card > span:last-child {
  color: #1ba7ce;
}

@media (min-width: 768px) {
  .tpl-sections,
  .tpl-related-grid,
  .tpl-retired-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .tpl-law-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 960px) {
  .tpl-hero-grid {
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
    gap: 4rem;
  }
}

@media (max-width: 640px) {
  .tpl-hero {
    padding: 7rem 0 3rem;
  }

  :deep(.tpl-download) {
    width: 100%;
    justify-content: center;
  }

  .tpl-preview {
    width: min(100%, 20rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  :deep(.tpl-download),
  .tpl-faq-item summary svg {
    transition: none;
  }

  :deep(.tpl-download:hover) {
    transform: none;
  }
}

/* The phone trial bar's buttons take the 8px radius of this page's download buttons (house rule: buttons 6-8px). */
.tpl-page :deep(.psc-primary),
.tpl-page :deep(.psc-secondary) {
  border-radius: 8px;
}
</style>
